import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Clock, Target, Briefcase, ExternalLink } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function ServiceDetailsModal({ service, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    // Contextual chat invite: after 25s on this modal, ping the chat widget
    // to auto-open with a primed message about this service. ChatWidget
    // dedupes via sessionStorage so it never feels intrusive.
    const inviteTimer = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("quasar:contextual-open", {
          detail: { kind: "service", name: service?.name },
        })
      );
    }, 25000);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      clearTimeout(inviteTimer);
    };
  }, [onClose, service]);

  if (!service) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
        onClick={onClose}
        data-testid="service-details-modal"
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[#0B0B0E] border border-white/10 glow-purple"
        >
          <div className="sticky top-0 z-20 flex justify-end px-4 pt-4 pb-2 bg-gradient-to-b from-[#0B0B0E] via-[#0B0B0E]/95 to-transparent pointer-events-none">
            <button
              onClick={onClose}
              className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full glass-strong hover:bg-white/10 transition-colors shadow-lg"
              data-testid="service-modal-close"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="px-6 sm:px-10 pb-6 sm:pb-10 -mt-3">
            <div className="text-[10px] font-mono-acc uppercase tracking-[0.28em] text-cyan-300 mb-2">
              / Service Detail
            </div>
            <h3 className="font-clash text-2xl sm:text-4xl font-semibold tracking-tight">
              {service.name}
            </h3>
            <p className="mt-2 text-cyan-300/90 text-sm font-mono-acc">{service.tagline}</p>

            <p className="mt-6 text-zinc-200 leading-relaxed text-[15px]">
              {service.overview}
            </p>

            {/* Deliverables */}
            <Section icon={Briefcase} label="What you get">
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                {service.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                    <CheckCircle2 size={13} className="text-cyan-300 mt-0.5 shrink-0" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </Section>

            {/* Milestones */}
            <Section icon={Clock} label="How we work — 90-day milestones">
              <div className="grid sm:grid-cols-3 gap-3">
                {service.milestones.map((m, i) => (
                  <div key={i} className="rounded-xl glass p-4">
                    <div className="text-[10px] font-mono-acc uppercase tracking-[0.22em] text-cyan-400 mb-2">
                      {m.day}
                    </div>
                    <div className="text-sm text-zinc-200 leading-snug">{m.body}</div>
                  </div>
                ))}
              </div>
            </Section>

            {/* KPIs */}
            <Section icon={Target} label="Typical outcomes (calibrated by category)">
              <div className="grid sm:grid-cols-2 gap-3">
                {service.kpis.map((k, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-xl bg-gradient-to-br from-cyan-500/5 via-purple-500/5 to-fuchsia-500/5 border border-white/[0.06] p-3">
                    <span className="font-mono-acc text-cyan-300 text-xs mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-sm text-zinc-200 leading-snug">{k}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Recent engagements with detailed working examples */}
            <Section icon={CheckCircle2} label="Recent client engagements">
              <ul className="space-y-3">
                {service.industries.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
                    <span className="text-cyan-300 mt-1">›</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </Section>

            {/* CTA */}
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:scale-[1.03] active:scale-[0.98] transition-transform"
                data-testid={`service-modal-cta-${service.key}`}
              >
                Book Strategy Call →
              </a>
              <Link
                to={`/services/${service.key}`}
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-full glass-strong px-6 py-3 text-sm hover:bg-white/10 transition-colors"
                data-testid={`service-modal-deep-${service.key}`}
              >
                Open full {service.name} page
                <ExternalLink size={13} />
              </Link>
              <a
                href="#audit"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-full glass px-6 py-3 text-sm hover:bg-white/10 transition-colors"
              >
                Run a Free Audit
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Section({ icon: Icon, label, children }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 text-[11px] font-mono-acc uppercase tracking-[0.22em] text-zinc-400 mb-4">
        <Icon size={12} className="text-cyan-300" /> {label}
      </div>
      {children}
    </div>
  );
}
